// src/lib/reportExport.ts
import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, ImageRun, convertInchesToTwip, Packer } from 'docx';
import { jsPDF } from 'jspdf';
import ProvafiLogo from '../assets/Dark-cropped-qoe-report.png';

const DISCLAIMER_TEXT = `DISCLAIMER: This Quality of Earnings report was generated using AI-powered analysis tools provided by Provafi. While we strive for accuracy, this report should be used as a preliminary analysis tool and not as a substitute for professional accounting advice or comprehensive due diligence. Users should verify all findings with qualified financial professionals before making any business decisions.`;

const ASSUMPTIONS_AND_LIMITING_CONDITIONS = {
  title: 'Assumptions and Limiting Conditions',
  sections: [
    {
      heading: 'Report Preparation and Intended Use',
      content: 'This Report has been prepared by Provafi, an AI-powered financial analysis software, at the direction of and solely for the intended user ("User"). Any recipient or reader of this Report ("Recipient") other than the User, by accessing this Report, agrees to and accepts the following terms and conditions.'
    },
    {
      heading: 'Confidentiality and Non-Disclosure',
      content: 'The subject matter of this Report and the information contained herein is confidential. Recipients shall not, directly or indirectly, disclose any information to any other person or entity, or reproduce, distribute, quote, or refer to this Report without express written consent from Provafi and the User.'
    },
    {
      heading: 'Limited Scope and No Reliance',
      content: 'This Report was prepared exclusively for the benefit of the User and may not include all due diligence procedures deemed necessary for the purposes of other Recipients. Certain findings and information may have been communicated to the User that are not reflected in this Report. Provafi makes no representations or warranties, express or implied, to any Recipient as to the accuracy, completeness, suitability, sufficiency, or appropriateness of the information for the purposes of any party other than the User. Recipients must perform their own independent due diligence procedures.'
    },
    {
      heading: 'Limitation of Liability',
      content: 'In no event, regardless of whether consent has been provided, shall Provafi or its affiliates assume any responsibility, liability, or duty of care to any person or entity other than the User. Provafi is owned by Stroube Consulting, LLC. No affiliate of Provafi or Stroube Consulting, LLC, nor their respective partners, principals, or employees involved in this matter, will have any liability in connection with this Report or the matters related hereto.'
    },
    {
      heading: 'Scope and Methodology Limitations',
      content: 'This Report is based solely on financial information and limited contextual data provided by the User. It does not constitute a full-scope Quality of Earnings assessment. The findings, conclusions, and assessments contained herein are subjective and may not be indicative of current or future performance. All business transactions are subject to operational and financial risks that may not have been captured or fully evaluated in this Report.'
    },
    {
      heading: 'IRS Circular 230 Disclosure',
      content: 'To ensure compliance with requirements imposed by the IRS, we inform you that any U.S. federal tax advice contained in this Report is not intended or written to be used, and cannot be used, for the purpose of (i) avoiding penalties under the Internal Revenue Code or (ii) promoting, marketing, or recommending to another party any transaction or matter addressed herein.'
    },
    {
      heading: 'Terms of Service and Privacy Policy',
      content: 'Use of and reliance on this Report is subject to Provafi\'s Terms of Service and Privacy Policy, available at www.provafi.com. By accessing this Report, you acknowledge that you have reviewed and agree to be bound by these terms.'
    },
    {
      heading: 'No Warranty or Guarantee',
      content: 'This Report is provided "as is" without warranties of any kind, either express or implied. Provafi disclaims all warranties, including but not limited to merchantability, fitness for a particular purpose, and non-infringement. The User and any Recipient assume all risks associated with the use of this Report and waive any claims against Provafi for damages or disruptions arising from reliance on the information contained herein.'
    }
  ]
};

interface ExportOptions {
  reportContent: string;
  projectName: string;
  reportTitle: string;
}

/**
 * Convert markdown-style text to Word document paragraphs
 */
function parseMarkdownToWordParagraphs(text: string): Paragraph[] {
  const lines = text.split('\n');
  const paragraphs: Paragraph[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('# ')) {
      // H1 heading
      paragraphs.push(
        new Paragraph({
          text: line.replace('# ', ''),
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 240, after: 120 },
          style: 'Heading1',
        })
      );
    } else if (line.startsWith('## ')) {
      // H2 heading
      paragraphs.push(
        new Paragraph({
          text: line.replace('## ', ''),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 180, after: 100 },
          style: 'Heading2',
        })
      );
    } else if (line.startsWith('### ')) {
      // H3 heading
      paragraphs.push(
        new Paragraph({
          text: line.replace('### ', ''),
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 120, after: 60 },
          style: 'Heading3',
        })
      );
    } else if (line.startsWith('#### ')) {
      // H4 heading
      paragraphs.push(
        new Paragraph({
          text: line.replace('#### ', ''),
          heading: HeadingLevel.HEADING_4,
          spacing: { before: 100, after: 50 },
          style: 'Heading4',
        })
      );
    } else if (line.startsWith('- ')) {
      // Bullet list item
      const listText = line.replace('- ', '');
      const textRuns = parseInlineFormatting(listText);
      paragraphs.push(
        new Paragraph({
          children: textRuns,
          bullet: { level: 0 },
          spacing: { before: 0, after: 60 },
        })
      );
    } else if (line.trim() === '') {
      // Skip empty lines - they add unnecessary spacing
      continue;
    } else if (line.trim() !== '') {
      // Regular paragraph
      const textRuns = parseInlineFormatting(line);
      paragraphs.push(
        new Paragraph({
          children: textRuns,
          spacing: { before: 0, after: 120 },
        })
      );
    }
  }

  return paragraphs;
}

/**
 * Parse inline formatting like **bold**
 */
function parseInlineFormatting(text: string): TextRun[] {
  const boldRegex = /\*\*(.*?)\*\*/g;
  const parts = text.split(boldRegex);
  const textRuns: TextRun[] = [];

  parts.forEach((part, index) => {
    if (index % 2 === 1) {
      // This is a bold part
      textRuns.push(new TextRun({ text: part, bold: true, font: 'Arial' }));
    } else if (part) {
      textRuns.push(new TextRun({ text: part, font: 'Arial' }));
    }
  });

  return textRuns.length > 0 ? textRuns : [new TextRun({ text, font: 'Arial' })];
}

/**
 * Load logo as base64 data URL (PNG format for Word compatibility)
 */
async function loadLogoAsBase64(): Promise<string> {
  try {
    const response = await fetch(ProvafiLogo);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading logo:', error);
    return '';
  }
}

/**
 * Convert base64 to Uint8Array for browser environment
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const base64Data = base64.split(',')[1];
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Export report as Word document (.docx)
 */
export async function exportToWord({ reportContent, projectName, reportTitle }: ExportOptions): Promise<Blob> {
  const logoBase64 = await loadLogoAsBase64();

  // Convert base64 to Uint8Array for docx library (browser-compatible)
  let logoData: Uint8Array | undefined;
  if (logoBase64) {
    logoData = base64ToUint8Array(logoBase64);
  }

  const paragraphs: Paragraph[] = [];

  // Add logo if available
  if (logoData) {
    paragraphs.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: logoData,
            transformation: {
              width: 200,
              height: 60,
            },
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      })
    );
  }

  // Add disclaimer
  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'DISCLAIMER',
          bold: true,
          size: 22, // 11pt
          font: 'Arial',
        }),
      ],
      spacing: { before: 120, after: 60 },
    })
  );

  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: DISCLAIMER_TEXT,
          size: 18, // 9pt
          italics: true,
          font: 'Arial',
        }),
      ],
      spacing: { after: 240 },
      border: {
        top: { color: 'CCCCCC', size: 6, style: 'single', space: 1 },
        bottom: { color: 'CCCCCC', size: 6, style: 'single', space: 1 },
        left: { color: 'CCCCCC', size: 6, style: 'single', space: 1 },
        right: { color: 'CCCCCC', size: 6, style: 'single', space: 1 },
      },
      shading: {
        fill: 'F9FAFB',
      },
    })
  );

  // Add report content
  const contentParagraphs = parseMarkdownToWordParagraphs(reportContent);
  paragraphs.push(...contentParagraphs);

  // Add page break before T&C section
  paragraphs.push(
    new Paragraph({
      text: '',
      pageBreakBefore: true,
    })
  );

  // Add Assumptions and Limiting Conditions section
  paragraphs.push(
    new Paragraph({
      text: ASSUMPTIONS_AND_LIMITING_CONDITIONS.title,
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 240, after: 180 },
      style: 'Heading1',
    })
  );

  ASSUMPTIONS_AND_LIMITING_CONDITIONS.sections.forEach((section) => {
    // Section heading
    paragraphs.push(
      new Paragraph({
        text: section.heading,
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 180, after: 100 },
        style: 'Heading2',
      })
    );

    // Section content
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: section.content,
            font: 'Arial',
          }),
        ],
        spacing: { before: 0, after: 120 },
      })
    );
  });

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: 'Arial',
            size: 20, // 10pt (size is in half-points)
          },
        },
      },
      paragraphStyles: [
        {
          id: 'Heading1',
          name: 'Heading 1',
          basedOn: 'Normal',
          next: 'Normal',
          run: {
            font: 'Arial',
            size: 28, // 14pt
            bold: true,
            color: '2E5090',
          },
          paragraph: {
            spacing: {
              before: 240,
              after: 120,
            },
          },
        },
        {
          id: 'Heading2',
          name: 'Heading 2',
          basedOn: 'Normal',
          next: 'Normal',
          run: {
            font: 'Arial',
            size: 24, // 12pt
            bold: true,
            color: '2E5090',
          },
          paragraph: {
            spacing: {
              before: 180,
              after: 100,
            },
          },
        },
        {
          id: 'Heading3',
          name: 'Heading 3',
          basedOn: 'Normal',
          next: 'Normal',
          run: {
            font: 'Arial',
            size: 22, // 11pt
            bold: true,
            color: '1F4E79',
          },
          paragraph: {
            spacing: {
              before: 120,
              after: 60,
            },
          },
        },
        {
          id: 'Heading4',
          name: 'Heading 4',
          basedOn: 'Normal',
          next: 'Normal',
          run: {
            font: 'Arial',
            size: 20, // 10pt
            bold: true,
            color: '1F4E79',
          },
          paragraph: {
            spacing: {
              before: 100,
              after: 50,
            },
          },
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
          },
        },
        children: paragraphs,
      },
    ],
  });

  // Generate blob
  return await Packer.toBlob(doc);
}

/**
 * Export report as PDF document
 */
export async function exportToPDF({ reportContent, projectName, reportTitle }: ExportOptions): Promise<Blob> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  // Add logo
  try {
    const logoBase64 = await loadLogoAsBase64();
    if (logoBase64) {
      const logoWidth = 60;
      const logoHeight = 18;
      const logoX = (pageWidth - logoWidth) / 2;
      pdf.addImage(logoBase64, 'PNG', logoX, yPosition, logoWidth, logoHeight);
      yPosition += logoHeight + 10;
    }
  } catch (error) {
    console.error('Error adding logo to PDF:', error);
  }

  // Add disclaimer box
  pdf.setFillColor(249, 250, 251);
  pdf.rect(margin, yPosition, contentWidth, 40, 'F');
  pdf.setDrawColor(204, 204, 204);
  pdf.rect(margin, yPosition, contentWidth, 40, 'S');

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('DISCLAIMER', margin + 5, yPosition + 7);

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'italic');
  const disclaimerLines = pdf.splitTextToSize(DISCLAIMER_TEXT, contentWidth - 10);
  pdf.text(disclaimerLines, margin + 5, yPosition + 12);
  yPosition += 50;

  // Add report content with improved formatting
  const lines = reportContent.split('\n');

  const addWrappedText = (text: string, x: number, fontSize: number, isBold: boolean = false) => {
    // Check for inline bold formatting
    const boldRegex = /\*\*(.*?)\*\*/g;

    if (boldRegex.test(text)) {
      // Has bold formatting - process inline
      const parts = text.split(boldRegex);
      let currentX = x;

      parts.forEach((part, index) => {
        if (part) {
          if (index % 2 === 1) {
            // Bold part
            pdf.setFont('helvetica', 'bold');
          } else {
            // Normal part
            pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
          }

          const wrappedLines = pdf.splitTextToSize(part, contentWidth - (currentX - margin));
          wrappedLines.forEach((wrappedLine: string, i: number) => {
            if (i > 0) {
              yPosition += fontSize * 0.35;
              currentX = x;

              // Check if we need a new page
              if (yPosition > pageHeight - margin - 10) {
                pdf.addPage();
                yPosition = margin;
              }
            }
            pdf.text(wrappedLine, currentX, yPosition);
            currentX += pdf.getTextWidth(wrappedLine);
          });
        }
      });

      return parts.filter(p => p).length > 0;
    } else {
      // No bold formatting - simple wrap
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      const wrappedText = pdf.splitTextToSize(text, contentWidth);

      wrappedText.forEach((wrappedLine: string, i: number) => {
        if (i > 0) {
          yPosition += fontSize * 0.35;

          // Check if we need a new page
          if (yPosition > pageHeight - margin - 10) {
            pdf.addPage();
            yPosition = margin;
          }
        }
        pdf.text(wrappedLine, x, yPosition);
      });

      return wrappedText.length > 0;
    }
  };

  for (const line of lines) {
    // Check if we need a new page
    if (yPosition > pageHeight - margin - 10) {
      pdf.addPage();
      yPosition = margin;
    }

    if (line.startsWith('# ')) {
      // H1 heading - 14pt with spacing before/after
      yPosition += 6; // spacing before
      pdf.setFontSize(14);
      addWrappedText(line.replace('# ', ''), margin, 14, true);
      yPosition += 8; // spacing after
    } else if (line.startsWith('## ')) {
      // H2 heading - 12pt with spacing
      yPosition += 5; // spacing before
      pdf.setFontSize(12);
      addWrappedText(line.replace('## ', ''), margin, 12, true);
      yPosition += 7; // spacing after
    } else if (line.startsWith('### ')) {
      // H3 heading - 11pt with spacing
      yPosition += 4; // spacing before
      pdf.setFontSize(11);
      addWrappedText(line.replace('### ', ''), margin, 11, true);
      yPosition += 5; // spacing after
    } else if (line.startsWith('#### ')) {
      // H4 heading - 10pt with spacing
      yPosition += 3; // spacing before
      pdf.setFontSize(10);
      addWrappedText(line.replace('#### ', ''), margin, 10, true);
      yPosition += 4; // spacing after
    } else if (line.startsWith('- ')) {
      // Bullet list - 10pt
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const bulletText = line.replace('- ', '');

      pdf.text('•', margin, yPosition);
      addWrappedText(bulletText, margin + 5, 10);
      yPosition += 5; // spacing after bullet
    } else if (line.trim() === '') {
      // Skip empty lines - Word doc skips them too
      continue;
    } else if (line.trim() !== '') {
      // Regular paragraph - 10pt
      pdf.setFontSize(10);
      addWrappedText(line, margin, 10);
      yPosition += 6; // spacing after paragraph
    }
  }

  return pdf.output('blob');
}

/**
 * Download a blob with a given filename
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
